import { useEffect, useState, useRef, useCallback } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  ListOrdered,
  List,
  Quote,
  Code,
  Link as LinkIcon,
  ChevronRight,
  FileText,
  Mic,
} from 'lucide-react';
import autocompleteService, { AutocompleteContext } from '@/services/autocomplete.service';
import { useTranslation } from 'react-i18next';

export type SlashCommandState = {
  isOpen: boolean;
  query: string;
  position: { top: number; left: number };
};

const MenuButton = ({
  isActive,
  onClick,
  icon: Icon,
  label,
}: {
  isActive?: boolean;
  onClick: () => void;
  icon: typeof Bold;
  label: string;
}) => (
  <Button
    type="button"
    size="icon"
    variant={isActive ? 'default' : 'ghost'}
    onClick={onClick}
    className={cn('h-8 w-8 rounded-sm', !isActive && 'text-muted-foreground')}
    aria-label={label}
  >
    <Icon className="h-4 w-4" />
  </Button>
);

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onSubmit?: () => void;
  actionButton?: React.ReactNode;
  isLoading?: boolean; // For neon glow effect during AI generation
  onImagePaste?: (files: File[]) => void; // Callback for pasted images from clipboard
  // Slash command support for snippets
  slashCommandState?: SlashCommandState;
  onSlashCommandChange?: (state: SlashCommandState) => void;
  onSlashCommandSelect?: () => void; // Called when snippet is selected to clean up the slash
  // Autocomplete support
  enableAutocomplete?: boolean;
  autocompleteContext?: AutocompleteContext;
};

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  className,
  disabled = false,
  onSubmit,
  actionButton,
  isLoading = false,
  onImagePaste,
  slashCommandState,
  onSlashCommandChange,
  onSlashCommandSelect,
  enableAutocomplete = false,
  autocompleteContext,
}: RichTextEditorProps) => {
  const { t } = useTranslation();
  const [toolbarExpanded, setToolbarExpanded] = useState(false);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const slashStartPosRef = useRef<number | null>(null);

  // Speech-to-text (Web Speech API — Chrome/Edge). Sprache automatisch aus Browser/Org.
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const wantListeningRef = useRef(false);
  const sttSupported =
    typeof window !== 'undefined' &&
    !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);
  // Org/Browser-Sprache → de-DE (Standard) oder en-US
  const sttLang =
    typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('en')
      ? 'en-US'
      : 'de-DE';

  // Autocomplete state
  const [suggestion, setSuggestion] = useState<string>('');
  const [suggestionPosition, setSuggestionPosition] = useState<{ top: number; left: number } | null>(null);
  const autocompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTextRef = useRef<string>('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
        blockquote: {
          HTMLAttributes: {
            class: 'border-l-4 border-slate-300 pl-4 italic text-slate-600',
          },
        },
        codeBlock: {
          HTMLAttributes: {
            class: 'bg-slate-100 rounded-md p-3 font-mono text-sm',
          },
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noreferrer noopener',
          class: 'text-primary underline underline-offset-2',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || '',
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[140px] max-h-[300px] overflow-y-auto text-sm text-slate-800 dark:text-slate-100 prose-blockquote:border-l-4 prose-blockquote:border-slate-300 prose-blockquote:pl-4 prose-blockquote:italic prose-pre:bg-slate-100 prose-pre:rounded-md prose-pre:p-3',
      },
      handlePaste: (view, event) => {
        // Check if clipboard contains image files
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        const items = clipboardData.items;
        const imageFiles: File[] = [];

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              imageFiles.push(file);
            }
          }
        }

        // If we found image files in clipboard, handle them
        if (imageFiles.length > 0 && onImagePaste) {
          event.preventDefault();
          onImagePaste(imageFiles);
          return true;
        }

        // Let TipTap handle other paste operations (text, etc.)
        return false;
      },
    },
    onUpdate({ editor }) {
      const html = editor.getHTML();
      onChange(html);

      // Clear suggestion when text changes
      if (suggestion) {
        setSuggestion('');
        setSuggestionPosition(null);
      }

      // Slash command detection
      if (onSlashCommandChange) {
        const { from } = editor.state.selection;
        const text = editor.state.doc.textBetween(0, from, ' ');

        // Find if we're in a slash command context
        const lastSlashIndex = text.lastIndexOf('/');

        if (lastSlashIndex !== -1) {
          const textAfterSlash = text.slice(lastSlashIndex + 1);
          // Check if there's no space after the slash (still typing the command)
          if (!textAfterSlash.includes(' ') && !textAfterSlash.includes('\n')) {
            // Get cursor position for popover
            const coords = editor.view.coordsAtPos(from);
            const containerRect = editorContainerRef.current?.getBoundingClientRect();

            if (containerRect) {
              slashStartPosRef.current = lastSlashIndex;
              onSlashCommandChange({
                isOpen: true,
                query: textAfterSlash,
                position: {
                  top: coords.bottom - containerRect.top + 4,
                  left: coords.left - containerRect.left,
                },
              });
              return;
            }
          }
        }

        // Close popover if no slash context
        if (slashCommandState?.isOpen) {
          slashStartPosRef.current = null;
          onSlashCommandChange({
            isOpen: false,
            query: '',
            position: { top: 0, left: 0 },
          });
        }
      }

      // Autocomplete logic
      if (enableAutocomplete && !slashCommandState?.isOpen) {
        const plainText = editor.state.doc.textContent;

        // Cancel previous timeout
        if (autocompleteTimeoutRef.current) {
          clearTimeout(autocompleteTimeoutRef.current);
        }

        // Don't fetch if text hasn't changed meaningfully
        if (plainText === lastTextRef.current) return;

        // Don't fetch if only a space was added (suggestion already exists for the word)
        const trimmedCurrent = plainText.trim();
        const trimmedLast = lastTextRef.current.trim();
        if (trimmedCurrent === trimmedLast && suggestion) {
          lastTextRef.current = plainText;
          return;
        }

        lastTextRef.current = plainText;

        // Debounce: wait 500ms after user stops typing
        autocompleteTimeoutRef.current = setTimeout(async () => {
          // Only fetch if we have enough text
          if (plainText.trim().length < 15) return;

          // Don't fetch if text ends with space (user is about to type more)
          if (plainText.endsWith(' ')) return;

          // Don't fetch if text ends with punctuation (sentence complete)
          const lastChar = plainText.trim().slice(-1);
          if (['.', '!', '?', ':', ';'].includes(lastChar)) return;

          try {
            const result = await autocompleteService.getSuggestion(plainText, autocompleteContext);

            if (result && result.trim()) {
              // Get cursor position for ghost text
              const { from } = editor.state.selection;
              const coords = editor.view.coordsAtPos(from);
              const containerRect = editorContainerRef.current?.getBoundingClientRect();

              if (containerRect) {
                setSuggestion(result);
                // Position exactly at cursor - relative to the editor content area
                // containerRef is the whole editor, toolbar is ~44px, padding is 12px
                const toolbarHeight = 44;
                setSuggestionPosition({
                  top: coords.top - containerRect.top - toolbarHeight + 12,
                  left: coords.left - containerRect.left + 12,
                });
              }
            }
          } catch (error) {
            console.error('Autocomplete error:', error);
          }
        }, 500);
      }
    },
    autofocus: false,
    editable: !disabled,
  });

  // Accept suggestion with Tab key
  useEffect(() => {
    if (!editor || !suggestion) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && suggestion) {
        event.preventDefault();

        const currentText = editor.state.doc.textContent;
        const { from } = editor.state.selection;

        // Find the current partial word (from last space to cursor)
        const textBeforeCursor = currentText.slice(0, from - 1); // -1 because doc pos starts at 1
        const lastSpaceIndex = textBeforeCursor.lastIndexOf(' ');
        const partialWord = textBeforeCursor.slice(lastSpaceIndex + 1).toLowerCase();

        // Check if suggestion is completing the partial word
        const suggestionLower = suggestion.toLowerCase();
        const suggestionFirstWord = suggestion.split(' ')[0].toLowerCase();

        let textToInsert = suggestion;
        let deleteChars = 0;

        if (partialWord && suggestionFirstWord.startsWith(partialWord) && partialWord !== suggestionFirstWord) {
          // Suggestion completes the word - delete partial and insert full suggestion
          deleteChars = partialWord.length;
          textToInsert = suggestion;
        } else if (partialWord && suggestionLower.startsWith(partialWord)) {
          // Suggestion starts with what user typed - delete partial and insert suggestion
          deleteChars = partialWord.length;
          textToInsert = suggestion;
        } else {
          // Suggestion is a new word - add space if needed
          const needsSpace = currentText.length > 0 && !currentText.endsWith(' ');
          textToInsert = needsSpace ? ' ' + suggestion : suggestion;
        }

        // Delete partial word if needed, then insert
        if (deleteChars > 0) {
          editor
            .chain()
            .focus()
            .deleteRange({ from: from - deleteChars, to: from })
            .insertContent(textToInsert)
            .run();
        } else {
          editor.chain().focus().insertContent(textToInsert).run();
        }

        setSuggestion('');
        setSuggestionPosition(null);
      } else if (event.key === 'Escape') {
        // Clear suggestion on Escape
        setSuggestion('');
        setSuggestionPosition(null);
      }
    };

    const element = editor.view.dom;
    element.addEventListener('keydown', handleKeyDown);

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor, suggestion]);

  useEffect(() => {
    if (!editor) return;

    if (value && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }

    if (!value && editor.getHTML() !== '<p></p>') {
      editor.commands.clearContent(true);
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;

    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor || !onSubmit) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't submit if there's a suggestion (Tab should accept it instead)
      if (event.key === 'Enter' && !event.shiftKey && !suggestion) {
        event.preventDefault();
        onSubmit();
      }
    };

    const element = editor.view.dom;
    element.addEventListener('keydown', handleKeyDown);

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor, onSubmit, suggestion]);

  // Function to remove slash command text from editor
  const removeSlashCommand = useCallback(() => {
    if (!editor || slashStartPosRef.current === null) return;

    const { from } = editor.state.selection;
    const deleteFrom = slashStartPosRef.current;

    // Delete from slash to current cursor position
    editor
      .chain()
      .focus()
      .deleteRange({ from: deleteFrom + 1, to: from + 1 }) // +1 because doc starts at 1
      .run();

    slashStartPosRef.current = null;
  }, [editor]);

  // Expose removeSlashCommand to parent via callback when selection happens
  useEffect(() => {
    if (onSlashCommandSelect && slashCommandState?.isOpen) {
      // Store the function reference so parent can call it
      (window as any).__richTextEditorRemoveSlash = removeSlashCommand;
    }
    return () => {
      delete (window as any).__richTextEditorRemoveSlash;
    };
  }, [onSlashCommandSelect, slashCommandState?.isOpen, removeSlashCommand]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autocompleteTimeoutRef.current) {
        clearTimeout(autocompleteTimeoutRef.current);
      }
    };
  }, []);

  // --- Speech-to-text (Spracheingabe) ---
  const stopDictation = useCallback(() => {
    wantListeningRef.current = false;
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.stop(); } catch { /* noop */ }
    }
    recognitionRef.current = null;
    setIsListening(false);
  }, []);

  const startDictation = useCallback(() => {
    if (!editor) return;
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    rec.lang = sttLang;
    rec.continuous = true;
    rec.interimResults = false;

    rec.onresult = (event: any) => {
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) finalText += res[0].transcript;
      }
      const text = finalText.trim();
      if (!text) return;
      // Führendes Leerzeichen einfügen, falls nötig
      const docText = editor.state.doc.textContent;
      const needsSpace = docText.length > 0 && !/\s$/.test(docText);
      editor.chain().focus().insertContent((needsSpace ? ' ' : '') + text + ' ').run();
    };

    rec.onerror = () => { stopDictation(); };

    rec.onend = () => {
      // continuous kann nach Pausen stoppen — neu starten, solange gewünscht
      if (wantListeningRef.current && recognitionRef.current === rec) {
        try { rec.start(); } catch { stopDictation(); }
      }
    };

    recognitionRef.current = rec;
    wantListeningRef.current = true;
    try {
      rec.start();
      setIsListening(true);
    } catch {
      stopDictation();
    }
  }, [editor, sttLang, stopDictation]);

  const toggleDictation = useCallback(() => {
    if (isListening) stopDictation();
    else startDictation();
  }, [isListening, startDictation, stopDictation]);

  // Diktat beenden, wenn Editor deaktiviert wird oder Komponente unmountet
  useEffect(() => {
    if (disabled && isListening) stopDictation();
  }, [disabled, isListening, stopDictation]);

  useEffect(() => {
    return () => { stopDictation(); };
  }, [stopDictation]);

  if (!editor) {
    return (
      <div
        className={cn(
          'flex min-h-[140px] items-center justify-center rounded-md border border-slate-200 bg-white text-sm text-slate-500 shadow-sm',
          className,
        )}
      >
        {t('common:x4.editorLoading', 'Editor wird geladen…')}
      </div>
    );
  }

  const setLink = () => {
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    // If link is active, remove it
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    // If no text selected, show warning
    if (!hasSelection) {
      alert(t('common:x4.selectTextForLink', 'Bitte wählen Sie zuerst einen Text aus, um einen Link hinzuzufügen.'));
      return;
    }

    const url = window.prompt('Link URL eingeben', 'https://');

    if (url === null || url === '' || url === 'https://') return;

    editor
      .chain()
      .focus()
      .setLink({ href: url })
      .run();
  };

  return (
    <div
      ref={editorContainerRef}
      className={cn(
        'relative rounded-lg border shadow-sm bg-white dark:bg-slate-900 transition-all duration-300 overflow-hidden',
        disabled && 'opacity-70 pointer-events-none',
        isLoading
          ? 'border-[#0F99FD] border-2'
          : 'border-slate-200',
        className,
      )}
      style={isLoading ? {
        animation: 'neonGlow 1.5s ease-in-out infinite alternate',
        borderRadius: '0.5rem',
      } : undefined}
    >
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes neonGlow {
          from {
            box-shadow: 0 0 8px rgba(15, 153, 253, 0.5), 0 0 16px rgba(15, 153, 253, 0.3), 0 0 24px rgba(15, 153, 253, 0.15);
            border-color: rgba(15, 153, 253, 0.8);
          }
          to {
            box-shadow: 0 0 16px rgba(15, 153, 253, 0.7), 0 0 32px rgba(15, 153, 253, 0.5), 0 0 48px rgba(15, 153, 253, 0.25);
            border-color: rgba(15, 153, 253, 1);
          }
        }
      `}} />
      <div className="rich-text-editor-toolbar flex items-center justify-between border-b border-slate-200 bg-slate-50 p-2">
        <div className="rich-text-editor-toolgroup flex items-center gap-1">
          <MenuButton
            icon={Bold}
            label="Fett"
            isActive={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          {sttSupported && (
            <Button
              type="button"
              size="icon"
              variant={isListening ? 'default' : 'ghost'}
              onClick={toggleDictation}
              disabled={disabled}
              className={cn(
                'h-8 w-8 rounded-sm',
                isListening
                  ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse'
                  : 'text-muted-foreground',
              )}
              aria-label={isListening ? 'Diktat stoppen' : 'Spracheingabe'}
              title={
                isListening
                  ? 'Aufnahme läuft… zum Stoppen klicken'
                  : `Spracheingabe (${sttLang === 'en-US' ? 'EN' : 'DE'})`
              }
            >
              <Mic className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => setToolbarExpanded(!toolbarExpanded)}
            className="h-8 w-8 rounded-full text-muted-foreground hover:bg-slate-200 transition-all"
            aria-label={toolbarExpanded ? 'Toolbar schließen' : 'Toolbar öffnen'}
          >
            <ChevronRight className={cn('h-4 w-4 transition-transform duration-300', toolbarExpanded && 'rotate-180')} />
          </Button>
          <div
            className={cn(
              'flex items-center gap-1 overflow-hidden transition-all duration-300 ease-in-out',
              toolbarExpanded ? 'max-w-[500px] opacity-100' : 'max-w-0 opacity-0'
            )}
          >
            <MenuButton
              icon={Italic}
              label="Kursiv"
              isActive={editor.isActive('italic')}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <MenuButton
              icon={UnderlineIcon}
              label="Unterstrichen"
              isActive={editor.isActive('underline')}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <MenuButton
              icon={Strikethrough}
              label="Durchgestrichen"
              isActive={editor.isActive('strike')}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            />
            <MenuButton
              icon={List}
              label="Aufzählung"
              isActive={editor.isActive('bulletList')}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            />
            <MenuButton
              icon={ListOrdered}
              label="Nummeriert"
              isActive={editor.isActive('orderedList')}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            />
            <MenuButton
              icon={Quote}
              label="Zitat"
              isActive={editor.isActive('blockquote')}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
            />
            <MenuButton
              icon={Code}
              label="Codeblock"
              isActive={editor.isActive('codeBlock')}
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            />
            <MenuButton
              icon={LinkIcon}
              label="Link"
              isActive={editor.isActive('link')}
              onClick={setLink}
            />
          </div>
        </div>
        {actionButton && (
          <div className="rich-text-editor-actiongroup ml-2 flex-shrink-0">
            {actionButton}
          </div>
        )}
      </div>
      <div
        className="rich-text-editor-content relative"
        onMouseDown={(event) => {
          if (disabled) return;

          const target = event.target as HTMLElement;
          if (target.closest('.ProseMirror')) return;

          event.preventDefault();
          editor.chain().focus('end').run();
        }}
      >
        <EditorContent editor={editor} className="rich-text-editor-surface p-3" />

        {/* Inline ghost text suggestion */}
        {suggestion && suggestionPosition && (
          <span
            className="pointer-events-none absolute text-sm text-slate-400/60 whitespace-pre"
            style={{
              top: suggestionPosition.top,
              left: suggestionPosition.left,
            }}
          >
            {suggestion}
            <span className="ml-1 text-[9px] text-slate-300 opacity-70">⇥</span>
          </span>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;
